import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const categoryImages: Record<string, string> = {
    "Shoes": "https://readdy.ai/api/search-image?query=Single%20white%20sneaker%20on%20beige%20background%20minimal%20product%20shot&width=80&height=80&seq=mob-cat-shoes&orientation=squarish",
    "Hoodies/Sweaters": "https://readdy.ai/api/search-image?query=Single%20oatmeal%20hoodie%20on%20cream%20background%20minimal%20product%20shot&width=80&height=80&seq=mob-cat-hoodies&orientation=squarish",
    "T-Shirts": "https://readdy.ai/api/search-image?query=Single%20white%20tshirt%20on%20beige%20background%20minimal%20product%20shot&width=80&height=80&seq=mob-cat-tshirts&orientation=squarish",
    "Jackets": "https://readdy.ai/api/search-image?query=Single%20olive%20jacket%20on%20cream%20background%20minimal%20product%20shot&width=80&height=80&seq=mob-cat-jackets&orientation=squarish",
    "Pants/Shorts": "https://readdy.ai/api/search-image?query=Single%20beige%20pants%20on%20linen%20background%20minimal%20product%20shot&width=80&height=80&seq=mob-cat-pants&orientation=squarish",
    "Headwear": "https://readdy.ai/api/search-image?query=Single%20cream%20cap%20on%20beige%20background%20minimal%20product%20shot&width=80&height=80&seq=mob-cat-headwear&orientation=squarish",
    "Sets": "https://readdy.ai/api/search-image?query=Matching%20tracksuit%20set%20on%20cream%20background%20minimal%20product%20shot&width=80&height=80&seq=mob-cat-sets&orientation=squarish",
    "Underwear/Underpants": "https://readdy.ai/api/search-image?query=Soft%20cotton%20underwear%20on%20linen%20background%20minimal%20product%20shot&width=80&height=80&seq=mob-cat-underwear&orientation=squarish",
    "Jersey": "https://readdy.ai/api/search-image?query=Red%20football%20jersey%20on%20cream%20background%20minimal%20product%20shot&width=80&height=80&seq=mob-cat-jersey&orientation=squarish",
    "Accessories": "https://readdy.ai/api/search-image?query=Tortoiseshell%20sunglasses%20on%20beige%20background%20minimal%20product%20shot&width=80&height=80&seq=mob-cat-accessories&orientation=squarish",
    "Bags": "https://readdy.ai/api/search-image?query=Cognac%20leather%20bag%20on%20cream%20background%20minimal%20product%20shot&width=80&height=80&seq=mob-cat-bags&orientation=squarish",
    "Belts": "https://readdy.ai/api/search-image?query=Brown%20leather%20belt%20on%20beige%20background%20minimal%20product%20shot&width=80&height=80&seq=mob-cat-belts&orientation=squarish",
    "Perfumes": "https://readdy.ai/api/search-image?query=Amber%20perfume%20bottle%20on%20cream%20background%20minimal%20product%20shot&width=80&height=80&seq=mob-cat-perfumes&orientation=squarish",
    "Other Stuff": "https://readdy.ai/api/search-image?query=Lifestyle%20items%20notebook%20mug%20on%20beige%20background%20minimal%20product%20shot&width=80&height=80&seq=mob-cat-other&orientation=squarish",
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-background-50/95 backdrop-blur-md' : 'bg-transparent'
      }`}
    >
      <div className="w-full px-6 md:px-10">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link to="/" className="flex items-center gap-2.5 cursor-pointer">
            <div className={`w-8 h-8 md:w-9 md:h-9 rounded-full flex items-center justify-center transition-colors duration-300 ${
              scrolled ? 'bg-primary-500' : 'bg-background-50/20'
            }`}>
              <span className={`font-heading text-sm font-bold transition-colors duration-300 ${
                scrolled ? 'text-background-50' : 'text-background-50'
              }`}>I</span>
            </div>
            <span className={`font-heading text-lg md:text-xl font-semibold whitespace-nowrap transition-colors duration-300 ${
              scrolled ? 'text-foreground-950' : 'text-background-50'
            }`}>
              Itaobuy Spreadsheet
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className={`font-label text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
              scrolled ? 'text-foreground-800 hover:text-primary-500' : 'text-background-50/90 hover:text-background-50'
            }`}>
              {t('nav.home')}
            </Link>
            <Link to="/itaobuy-spreadsheet" className={`font-label text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
              scrolled ? 'text-foreground-800 hover:text-primary-500' : 'text-background-50/90 hover:text-background-50'
            }`}>
              {t('nav.guide')}
            </Link>
            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className={`font-label text-sm font-medium transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1.5 ${
              scrolled ? 'text-foreground-800 hover:text-primary-500' : 'text-background-50/90 hover:text-background-50'
            }`}>
              Guida Acquisti
              <span className="w-3 h-3 flex items-center justify-center">
                <i className="ri-external-link-line text-[10px]"></i>
              </span>
            </a>
            <Link to="/database" className={`font-label text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
              scrolled ? 'text-foreground-800 hover:text-primary-500' : 'text-background-50/90 hover:text-background-50'
            }`}>
              {t('nav.database')}
            </Link>
            <Link to="/community" className={`font-label text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
              scrolled ? 'text-foreground-800 hover:text-primary-500' : 'text-background-50/90 hover:text-background-50'
            }`}>
              {t('nav.community')}
            </Link>

            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsOpen(!isOpen)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full border font-label text-sm transition-colors cursor-pointer whitespace-nowrap ${
                  scrolled
                    ? 'border-foreground-300 text-foreground-800 bg-background-50 hover:border-primary-300'
                    : 'border-background-50/40 text-background-50 bg-transparent hover:border-background-50/80'
                }`}
              >
                <span>{t('nav.category')}</span>
                {isOpen ? (
                  <i className="ri-arrow-up-s-line text-xs"></i>
                ) : (
                  <i className="ri-arrow-down-s-line text-xs"></i>
                )}
              </button>

              {isOpen && (
                <div className="absolute right-0 mt-2 w-72 bg-background-50 border border-background-200 rounded-lg overflow-hidden max-h-[70vh] overflow-y-auto shadow-sm">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-3 text-sm text-foreground-800 hover:bg-accent-100 hover:text-accent-800 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <div className="w-8 h-8 rounded-lg overflow-hidden bg-background-100 flex-shrink-0">
                        <img src={categoryImages[cat.name]} alt={cat.name} className="w-full h-full object-cover" />
                      </div>
                      <div>
                        <span className="font-label text-xs font-semibold">{cat.name}</span>
                        <p className="font-body text-[10px] text-foreground-500 leading-tight">{cat.desc}</p>
                      </div>
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden flex items-center justify-center w-10 h-10 rounded-lg cursor-pointer transition-colors duration-300 ${
              scrolled ? 'text-foreground-800' : 'text-background-50'
            }`}
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200 px-6 py-4 max-h-[80vh] overflow-y-auto">
          <div className="flex flex-col gap-1">
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-3 py-3 rounded-lg font-label text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
            >
              <span className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center">
                <i className="ri-home-line text-primary-600"></i>
              </span>
              {t('nav.home')}
            </Link>

            <Link
              to="/itaobuy-spreadsheet"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-3 py-3 rounded-lg font-label text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
            >
              <span className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center">
                <i className="ri-file-text-line text-primary-600"></i>
              </span>
              {t('nav.guide')}
            </Link>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-3 py-3 rounded-lg font-label text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
            >
              <span className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center">
                <i className="ri-shopping-cart-line text-accent-600"></i>
              </span>
              Guida Acquisti
              <span className="w-3 h-3 flex items-center justify-center ml-auto">
                <i className="ri-external-link-line text-foreground-400 text-[10px]"></i>
              </span>
            </a>

            <Link
              to="/database"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-3 py-3 rounded-lg font-label text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
            >
              <span className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center">
                <i className="ri-database-2-line text-primary-600"></i>
              </span>
              {t('nav.database')}
            </Link>

            <Link
              to="/community"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-3 py-3 rounded-lg font-label text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
            >
              <span className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center">
                <i className="ri-group-line text-primary-600"></i>
              </span>
              {t('nav.community')}
            </Link>

            <div className="border-t border-background-200 pt-3 mt-2">
              <button
                onClick={() => setMobileDropdownOpen(!mobileDropdownOpen)}
                className="flex items-center justify-between w-full px-3 py-3 rounded-lg font-label text-sm font-semibold text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
              >
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center">
                    <i className="ri-apps-line text-accent-600"></i>
                  </span>
                  {t('nav.category')}
                </div>
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className={`text-sm ${mobileDropdownOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-foreground-400`}></i>
                </span>
              </button>

              {mobileDropdownOpen && (
                <div className="grid grid-cols-2 gap-2 mt-2 px-1">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex flex-col items-center gap-2 p-3 rounded-lg hover:bg-accent-100 transition-colors cursor-pointer"
                    >
                      <div className="w-12 h-12 rounded-lg overflow-hidden bg-background-100">
                        <img src={categoryImages[cat.name]} alt={cat.name} className="w-full h-full object-cover" />
                      </div>
                      <span className="font-label text-[10px] font-semibold text-foreground-700 text-center leading-tight whitespace-nowrap">
                        {cat.name}
                      </span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <div className="border-t border-background-200 pt-3 mt-2">
              <p className="px-3 py-2 font-label text-xs font-semibold text-foreground-500 uppercase tracking-wider">Articoli SEO</p>
              <Link to="/come-funziona-itaobuy" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-2 px-3 py-2 text-sm text-foreground-700 hover:text-primary-500 transition-colors cursor-pointer">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-s-line text-xs"></i></span>Come Funziona
              </Link>
              <Link to="/itaobuy-guida-principianti" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-2 px-3 py-2 text-sm text-foreground-700 hover:text-primary-500 transition-colors cursor-pointer">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-s-line text-xs"></i></span>Guida Principianti
              </Link>
              <Link to="/confronto" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-2 px-3 py-2 text-sm text-foreground-700 hover:text-primary-500 transition-colors cursor-pointer">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-s-line text-xs"></i></span>Confronto
              </Link>
              <Link to="/prezzi" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-2 px-3 py-2 text-sm text-foreground-700 hover:text-primary-500 transition-colors cursor-pointer">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-s-line text-xs"></i></span>Prezzi
              </Link>
              <Link to="/guida-completa" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-2 px-3 py-2 text-sm text-foreground-700 hover:text-primary-500 transition-colors cursor-pointer">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-s-line text-xs"></i></span>Guida Completa
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}